import React, { useCallback, useState, useRef, useEffect }  from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import Button from '@mui/material/Button';
import {PinRounded, PinDropRounded, DeleteForeverRounded, AutoGraphRounded, AnalyticsRounded} from "@mui/icons-material";
import { Card, Empty, Spin, Row, Col, Space, Drawer, Popover, Checkbox } from "antd";
import {useEnsureTickersLoaded} from "../../../Hooks/useEnsureTickersLoaded";
import StockChart from '../../../charts/StockChart';
import Snapshot from "../../../Components/Snapshot/Snapshot";
import {useSnapshot} from "../../../Components/Snapshot/useSnapshot";
import useTickerStyles from "../../../Components/Snapshot/useTickerStyles";
import {useGptResponses} from "../../../Hooks/useGptResponses";
import {selectResponseById} from "../../../Store/slices/gpt.slice";
import {getPortfolioChangesPrompt} from "../portfolioPrompts";
import EditableResponse from "../../../Components/EditableResponse";
import {DisplayChangesPromptData} from "../extra/PromptDataDisplays";
import {percentReturn} from "../../../utils/math";
import YearlyReturnsTable from "../../../SuperComponents/DataDisplay/YearlyReturnsTable";
import {rewindowSeriesToMonthly, filterSeriesBetweenRange} from "../../../utils/dataTransforms";
import {getDeltaDaysFromTimestamps} from "../../../utils/datetime";
import PieGrid from "../../../SuperComponents/DataDisplay/PieGrid/PieGrid";
import {formatDataForPieGrid} from "../../../SuperComponents/DataDisplay/PieGrid/pieGridUtils";
import PortfolioAllocations from "../../PortfolioAllocations/PortfolioAllocations";
import {formatDataForStockChart} from "./utils";


const stackAllSnapshots = (selectedPoints)=>{
    let snapshots = [];
    selectedPoints.forEach(point=>{
        const items = point.items;
        const firstItem = items[0].data;
        const row = [...firstItem];
        for (let i=1; i<items.length; i++){
            row.push(items[i].data[1]);
        }
        snapshots.push(row);
    });
    snapshots.sort((a, b)=>a[0].localeCompare(b[0]))
    return snapshots;
}



const PlotHoldingsButton = ({ allTickers, selectedTickers, setSelectedTickers, onPlotHoldings }) => {
    const [visible, setVisible] = useState(false);

    const handleVisibleChange = (newVisible) => {
        setVisible(newVisible);
    };

    const handleTickerChange = (checkedValues) => {
        setSelectedTickers(checkedValues);
    };

    const tickerOptions = allTickers.map(ticker => ({ label: ticker.symbol, value: ticker.symbol }));
    const content = (
        <Checkbox.Group
            style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)' }}
            options={tickerOptions}
            value={selectedTickers}
            onChange={handleTickerChange}
        />
    );

    const count = selectedTickers.length;
    return (
        <Space>
            <Popover
                content={content}
                title="Select Tickers"
                trigger="click"
                visible={visible}
                onVisibleChange={handleVisibleChange}
            >
                <Button startIcon={<PinRounded />} variant={'outlined'}>
                    Select Tickers
                </Button>
            </Popover>
            <Button startIcon={<AnalyticsRounded />} variant={count > 0 ? 'contained' : 'text'} onClick={onPlotHoldings}>
                Plot Selected {count > 0 && `(${count})`}
            </Button>
        </Space>
    );
};



const ChartContainer = ({ portfolioTimeSeries, benchmarkTimeSeries, portfolio, categoryMapping, tickerLookup,
                        addTimeseriesSymbols, extraTimeseries, allTickers }) => {
    const chartRef = useRef(null);
    const [pieData, setPieData] = useState(null);
    const [promptRows, setPromptRows] = useState(null);
    const [showResponseModal, setShowResponseModal] = useState(false);
    const [showPies, setShowPies] = useState(false);
    const [selectedTickers, setSelectedTickers] = useState([]);

    const { responseId, submitPrompt } = useGptResponses();
    const { snapshotData, snapshotCallback } = useSnapshot();

    const selectedPoints = useSelector(state => state.appStates.selectedChartPoints);
    const gptResponse = useSelector(state => selectResponseById(state, responseId));


// useCallback to handle range changes
    const callbackOnRangeChange = useCallback((start, end) => {
      const days = getDeltaDaysFromTimestamps(start, end);
      if (days < 390) {
        const rewindowedSeries = rewindowSeriesToMonthly([...portfolioTimeSeries]);
        const filteredSeries = filterSeriesBetweenRange(rewindowedSeries, new Date(start), new Date(end));

        const unwindSnapshot = (snapshot)=>{
            return Object.fromEntries(Object.entries(snapshot).map(([key, value]) => [key, value.wgt]))
        }

        const newData = formatDataForPieGrid({
            data: filteredSeries.map(x=>{
                return {timestamp: x.timestamp, ...unwindSnapshot(x.snapshot)}
            }),
            idField: 'timestamp'
        });

        // Check if newData is different from the current pieData before updating
        if (JSON.stringify(newData) !== JSON.stringify(pieData)) {
          setPieData(newData);
          setShowPies(true);
        }
      } else {
        setShowPies(false);  // Hide pies if the condition is not met
      }
    }, [portfolioTimeSeries, pieData]);



    useEffect(()=>{
        if (selectedPoints.length===2){
            const [x1, x2] = [selectedPoints[0].timestamp, selectedPoints[1].timestamp].sort((a, b)=>a.localeCompare(b));
            const [y1, y2] = [selectedPoints[0].items[0].data[1], selectedPoints[1].items[0].data[1]];
            const y0 = portfolioTimeSeries[0].close;
            chartRef.current.drawAnnotation().drawLineSegment({x1, x2, y1: percentReturn(y1, y0)*100, y2: percentReturn(y2, y0)*100});
            chartRef.current.finishAnnotation();
            // selectRange(timestamps[0], timestamps[1]);
        }
    }, [selectedPoints]);


    const onPlotHoldings = () => {
        const currentTimeseriesSymbols = new Set([...portfolioTimeSeries, ...benchmarkTimeSeries, ...extraTimeseries].map(series => series.symbol));
        const symbolsToFetch = selectedTickers.length > 0 ? selectedTickers : allTickers.map(x => x.symbol);
        const symbolsToAdd = symbolsToFetch.filter(symbol => !currentTimeseriesSymbols.has(symbol));
        if (symbolsToAdd.length > 0) addTimeseriesSymbols(symbolsToAdd);
    };


    const dataSeries = formatDataForStockChart(portfolioTimeSeries, benchmarkTimeSeries, extraTimeseries, snapshotCallback, portfolio);
    const submitGptPrompt = useCallback(()=>{
        const snapshots = stackAllSnapshots(selectedPoints);
        const {promptString, rows} = getPortfolioChangesPrompt(snapshots, extraTimeseries && extraTimeseries.map(x=>x.symbol));
        submitPrompt(JSON.stringify(promptString));
        setShowResponseModal(true);
        setPromptRows(rows);

    }, [submitPrompt, selectedPoints, extraTimeseries]);

    const handleTickerChange = (checkedValues) => {
        setSelectedTickers(checkedValues);
    };

    // const PlotHoldingsButton = () => {
    //     const content = (
    //         <Checkbox.Group
    //             options={allTickers.map(ticker => ({ label: ticker.symbol, value: ticker.symbol }))}
    //             value={selectedTickers}
    //             onChange={handleTickerChange}
    //         />
    //     );
    //     const count = selectedTickers.length - extraTimeseries?.map(x=>x.symbol)?.length + 1 ?? 0;
    //     return (
    //         <Space>
    //             <Popover content={content} title="Select Tickers" trigger="hover">
    //                 <Button startIcon={<PinRounded />} variant={'outlined'}>
    //                     Select Tickers
    //                 </Button>
    //             </Popover>
    //             <Button startIcon={<AnalyticsRounded />} variant={count > 0 ? 'contained' : 'text'} onClick={onPlotHoldings}>
    //                   Plot Selected {count > 0 && `(${count})`}
    //             </Button>
    //         </Space>
    //
    //     );
    // };



    return (
        <Row  gutter={[-16, 0]}>
            <Col flex={'0 0 300px'}>
                <Snapshot snapshot={snapshotData} categoryMapping={categoryMapping} colorLookup={tickerLookup}/>
            </Col>
            <Col flex={'1 1'}>
                <Row justify={'space-between'} style={{marginBottom: '20px'}}>
                    {selectedPoints.length >  0 ? (
                        <Space>
                            <Space>
                                {selectedPoints.map((point, index)=>(
                                    <Button startIcon={<PinDropRounded/>}>{point.timestamp}</Button>
                                ))}
                            </Space>
                            <Button startIcon={<DeleteForeverRounded/>} color={'error'} onClick={()=>chartRef.current.clearAnnotations().clearSelectedPoints()}>Clear All</Button>
                            <Button variant={'contained'} startIcon={<AutoGraphRounded/>} onClick={submitGptPrompt}>
                                AI Insights
                            </Button>
                        </Space>
                    ) : <Space>No Selected Points</Space>}
                    <PlotHoldingsButton onPlotHoldings={onPlotHoldings} allTickers={allTickers} selectedTickers={selectedTickers} setSelectedTickers={setSelectedTickers} />
                </Row>
                <StockChart
                    height={600}
                    ref={chartRef}
                    dataSeries={[
                        {
                            plot: 0,
                            data: dataSeries,
                            yScale: 'percent',
                            yLabelPostfix: '%',
                            legend: {
                                format: "%",
                                precision: 2
                            },

                        }
                    ]}
                    customSnapshotCallback={snapshotCallback}
                    callbackOnRangeChange={callbackOnRangeChange}
                />
                {!showPies && (
                    <div style={{padding: '20px'}}>
                        <YearlyReturnsTable
                            seriesData={[
                                { name: portfolio?.name, data: portfolioTimeSeries },
                                { name: 'Benchmark', data: benchmarkTimeSeries }
                            ]}
                        />
                    </div>
                )}
                {showPies && (
                    <PieGrid
                        chartData={pieData.data}
                        colorMapping={categoryMapping}
                        fieldNames={pieData.fieldNames}
                        rows={16}
                    />

                )}
            </Col>
            {gptResponse && (
                <Drawer
                    title={'Portfolio Changes Explained'}
                    open={showResponseModal}
                    onClose={()=>setShowResponseModal(false)}
                    width={1200}
                >
                    <Row>
                        <Col span={12}>
                            <EditableResponse response={gptResponse} />
                        </Col>
                        <Col span={12}>
                            <DisplayChangesPromptData data={promptRows} />
                        </Col>
                    </Row>
                </Drawer>
            )}
        </Row>
    )
};



const OverviewV2 = ({portfolio, portfolioTimeSeries, benchmarkTimeSeries, isDataReady, addTimeseriesSymbols,
                    extraTimeseries, allTickers}) => {
    const superChartRef = useRef(null);
    const [showAllocations, setShowAllocations] = useState(false);
    const { tickersToStyles, categoryMapping } = useTickerStyles(portfolioTimeSeries, 'tags.style');


    return (
        <Card
            bordered={false}
        >
              {isDataReady ? (
                  <>
                      <ChartContainer
                          portfolioTimeSeries={portfolioTimeSeries}
                          benchmarkTimeSeries={benchmarkTimeSeries}
                          portfolio={portfolio}
                          categoryMapping={categoryMapping}
                          tickerLookup={tickersToStyles}
                          addTimeseriesSymbols={addTimeseriesSymbols}
                          extraTimeseries={extraTimeseries}
                          allTickers={allTickers}
                      />
                      {showAllocations ? (
                          <PortfolioAllocations pid={portfolio.id} chartRef={superChartRef} />
                      ) : (
                          <div>
                              <Button onClick={()=>{setShowAllocations(true)}}>SHOW ALLOCATIONS</Button>
                          </div>
                      )}
                  </>
              ) : (
                <Spin><Empty/> </Spin> // Placeholder for loading state or empty data
              )}
        </Card>
    )
}



export default OverviewV2;